/**
 * TAILWIND MODAL KOMPONENTE
 *
 * Wiederverwendbarer Modal-Dialog basierend auf HeadlessUI Dialog.
 * Escape-Taste, Overlay-Klick, Fokus-Trap und Animation eingebaut.
 *
 * Exportiert 3 Komponenten:
 *
 * --- TWModal ---
 *   isOpen     - boolean, steuert Sichtbarkeit
 *   onClose    - () => void, Callback beim Schließen
 *   title?     - string, zeigt Header mit Titel + X-Button
 *   icon?      - ReactNode, Icon links neben dem Titel
 *   size?      - sm (max-w-sm) | md (default, max-w-lg) | lg (max-w-2xl) | xl (max-w-4xl) | 2xl (max-w-6xl) | full (max-w-[95vw])
 *   className? - zusätzliche Klassen für das Panel
 *
 * --- TWModalFooter ---
 *   Platziert Buttons am unteren Rand mit Trennlinie und grauem Hintergrund.
 *   className? - zusätzliche Klassen
 *
 * --- TWModalButton ---
 *   variant?  - primary (default, bg-brand) | cancel (bg-gray-100) | danger (bg-red-600)
 *   onClick?  - () => void
 *   disabled? - boolean
 *   loading?  - boolean, zeigt Spinner + deaktiviert Button
 *   className? - zusätzliche Klassen
 *
 * Usage:
 *   <TWModal isOpen={show} onClose={() => setShow(false)} title="Bearbeiten" size="md">
 *     <p>Inhalt hier...</p>
 *     <TWModalFooter>
 *       <TWModalButton variant="cancel" onClick={() => setShow(false)}>Abbrechen</TWModalButton>
 *       <TWModalButton variant="primary" onClick={handleSave}>Speichern</TWModalButton>
 *     </TWModalFooter>
 *   </TWModal>
 */

import { Children, isValidElement } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { X } from 'lucide-react';
import clsx from 'clsx';
import { useSidebarContext } from '../../contexts/SidebarContext';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'wide' | 'full';

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  '2xl': 'max-w-6xl',
  // 'wide': füllt verfügbare Breite (Sidebar wird via padding-Inset abgezogen), gedeckelt bei 1600px
  wide: 'max-w-[1600px]',
  full: 'max-w-[95vw]',
};

// Höhen-Deckel pro Modal-Size: Modals wachsen mit ihrem Inhalt und sind
// bei diesem Wert hart gedeckelt. KEIN h-full — sonst wird das Modal
// auch bei wenig Inhalt künstlich auf den Deckel hochgezogen, und der
// Footer hängt mit großem Leerraum darüber.
// Arbeits-Modals (xl, 2xl, wide, full) bekommen 1100px Deckel; kleine
// Bestätigungsdialoge nutzen 90vh als weichen Viewport-Anteil.
const MAX_HEIGHT_CLASSES: Record<ModalSize, string> = {
  sm: 'max-h-[90vh]',
  md: 'max-h-[90vh]',
  lg: 'max-h-[90vh]',
  xl: 'max-h-[min(1100px,95vh)]',
  '2xl': 'max-h-[min(1100px,95vh)]',
  wide: 'max-h-[min(1100px,95vh)]',
  full: 'max-h-[min(1100px,95vh)]',
};

// Sidebar-Breite in px (siehe SidebarTW.tsx, SIDEBAR_WIDTH = 220).
// Wird via inline style auf den Modal-Container gelegt, damit das Modal nie
// unter die persistente Desktop-Sidebar rutscht.
const SIDEBAR_WIDTH_PX = 220;

// --- TWModal ---

interface TWModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  icon?: React.ReactNode;
  size?: ModalSize;
  children: React.ReactNode;
  className?: string;
}

export const TWModal: React.FC<TWModalProps> = ({
  isOpen,
  onClose,
  title,
  icon,
  size = 'md',
  children,
  className,
}) => {
  const { isMobile } = useSidebarContext();
  // Desktop: Modal-Bereich startet rechts der Sidebar — verhindert Verdecken auf
  // jeder Bildschirmbreite. Mobile/Tablet: Sidebar ist Overlay, kein Offset nötig.
  const containerStyle = isMobile ? undefined : { left: SIDEBAR_WIDTH_PX };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      {/* Backdrop — bleibt vollflächig (verdunkelt auch die Sidebar leicht) */}
      <DialogBackdrop
        transition
        data-lenis-prevent
        className={clsx(
          'fixed inset-0 bg-black/50 backdrop-blur-sm',
          'transition-opacity duration-200 ease-out',
          'data-[closed]:opacity-0'
        )}
      />

      {/* Container — startet auf Desktop hinter der Sidebar, damit das Modal
          immer im sichtbaren Content-Bereich zentriert wird, egal wie breit
          oder schmal der Viewport ist. data-lenis-prevent stoppt Lenis-Smooth-Scroll. */}
      <div
        className="fixed inset-y-0 right-0 left-0 flex items-center justify-center p-4"
        style={containerStyle}
        data-lenis-prevent
      >
        <DialogPanel
          transition
          className={clsx(
            'w-full flex flex-col',
            SIZE_CLASSES[size],
            // Höhe: Modal wächst mit seinem Inhalt, gedeckelt durch die
            // Size-spezifische Max-Höhe. Arbeits-Modals max. 1100px (oder
            // 95vh wenn das Viewport kleiner ist); Bestätigungsdialoge
            // max. 90vh. KEIN h-full — das würde bei wenig Inhalt einen
            // großen Leerraum zwischen Body und Footer erzeugen.
            MAX_HEIGHT_CLASSES[size],
            'bg-white dark:bg-gray-900',
            'rounded-xl shadow-2xl',
            'border border-gray-200 dark:border-white/10',
            'transition-all duration-200 ease-out',
            'data-[closed]:opacity-0 data-[closed]:scale-95',
            className
          )}
        >
          {/* Header */}
          {title && (
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-white/10">
              <DialogTitle className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
                {icon && <span className="shrink-0 text-gray-500 dark:text-gray-400">{icon}</span>}
                {title}
              </DialogTitle>
              <button
                onClick={onClose}
                aria-label="Schließen"
                className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Body + Footer-Extraktion:
              TWModalFooter wird aus children herausgefischt und als
              Geschwister-Element unterhalb des scroll-baren Bodies
              gerendert. Dadurch bleibt der Footer IMMER sichtbar, auch
              wenn der Body überläuft — Aufrufer-Syntax bleibt unverändert
              (Footer wird wie bisher INSIDE <TWModal> deklariert). */}
          {(() => {
            const childArray = Children.toArray(children);
            const footers = childArray.filter(
              (c) => isValidElement(c) && (c.type as { displayName?: string })?.displayName === 'TWModalFooter'
            );
            const bodyChildren = childArray.filter(
              (c) => !(isValidElement(c) && (c.type as { displayName?: string })?.displayName === 'TWModalFooter')
            );
            return (
              <>
                {/* Body (scrollbar bei Overflow, min-h-0 ermöglicht Schrumpfen in Flexbox) */}
                {/* data-lenis-prevent + onWheel stopPropagation: Hintergrund (Lenis) scrollt nicht mit */}
                <div
                  data-lenis-prevent
                  className="flex-1 min-h-0 overflow-y-auto overscroll-contain scrollbar-styled px-5 py-4"
                  onWheel={(e) => e.stopPropagation()}
                >
                  {bodyChildren}
                </div>
                {/* Footer als sticky-Geschwister: nicht im Scroll, immer sichtbar */}
                {footers}
              </>
            );
          })()}
        </DialogPanel>
      </div>
    </Dialog>
  );
};

// --- TWModalFooter ---

interface TWModalFooterProps {
  children: React.ReactNode;
  className?: string;
}

export const TWModalFooter: React.FC<TWModalFooterProps> = ({ children, className }) => {
  return (
    // Footer wird vom TWModal aus children extrahiert und als Geschwister
    // neben dem scroll-baren Body gerendert (shrink-0 = wird nicht
    // komprimiert, bleibt also immer sichtbar). Keine negativen Margins
    // mehr nötig — Footer steht außerhalb des Body-Paddings.
    <div
      className={clsx(
        'shrink-0',
        'flex items-center justify-end gap-3',
        'px-5 py-3',
        'border-t border-gray-200 dark:border-white/10',
        'bg-gray-50 dark:bg-gray-800/50',
        'rounded-b-xl',
        className
      )}
    >
      {children}
    </div>
  );
};

// Marker für die Footer-Extraktion in TWModal — siehe Body-Render-Block.
TWModalFooter.displayName = 'TWModalFooter';

// --- TWModalButton ---

type ButtonVariant = 'primary' | 'cancel' | 'danger';

interface TWModalButtonProps {
  variant?: ButtonVariant;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
  className?: string;
}

const BUTTON_CLASSES: Record<ButtonVariant, string> = {
  primary: 'bg-brand text-gray-950 hover:bg-brand-hover',
  cancel: 'bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/15',
  danger: 'bg-red-600 text-white hover:bg-red-700',
};

export const TWModalButton: React.FC<TWModalButtonProps> = ({
  variant = 'primary',
  onClick,
  disabled = false,
  loading = false,
  children,
  className,
}) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={clsx(
        'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        BUTTON_CLASSES[variant],
        className
      )}
    >
      {loading ? (
        <span className="inline-flex items-center gap-2">
          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          {children}
        </span>
      ) : (
        children
      )}
    </button>
  );
};
